import React, { useEffect, useMemo, useState } from "react";
import "./Admin.css";

// Per-viewer fleet picker for track.eim.digital. Each logged-in user has
// their own saved selection — changes here don't affect anyone else.
//
// Two modes (toggle):
//   'group'  — pick one or more customer-fleet groups (users in battery_swap_db).
//              Every truck under the picked groups is auto-included.
//   'trucks' — pick individual trucks from a flat list.
//
// Plus a free-form "Extras" list: plates or Intangles IDs that aren't in
// battery_swap_db at all (e.g. internal test rigs, demo vehicles).

export default function Admin() {
  const [catalog, setCatalog] = useState({ groups: [], trucks: [] });
  const [intangles, setIntangles] = useState({ vehicles: [], fetchedAt: 0 });
  const [sel, setSel] = useState({ mode: "group", group_ids: [], truck_ids: [], extra_keys: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [search, setSearch] = useState("");
  const [extraInput, setExtraInput] = useState("");
  const [showOnlyUnmapped, setShowOnlyUnmapped] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [cRes, iRes, sRes] = await Promise.all([
          fetch("/api/track/catalog", { credentials: "include" }),
          fetch("/api/track/intangles", { credentials: "include" }),
          fetch("/api/track/selection", { credentials: "include" }),
        ]);
        if (!cRes.ok) throw new Error("Failed to load catalog");
        if (!sRes.ok) throw new Error("Failed to load selection");
        const c = await cRes.json();
        const i = iRes.ok ? await iRes.json() : { vehicles: [] };
        const s = await sRes.json();
        if (cancelled) return;
        setCatalog(c);
        setIntangles(i);
        setSel({
          mode: s.mode || "group",
          group_ids: s.group_ids || [],
          truck_ids: s.truck_ids || [],
          extra_keys: s.extra_keys || [],
        });
      } catch (e) {
        if (!cancelled) setErr(e.message || "Load failed");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Plates that DO appear in battery_swap_db (used to filter "unmapped" extras).
  const mappedPlateSet = useMemo(() => {
    const s = new Set();
    for (const t of catalog.trucks) {
      if (t.license_plate) s.add(String(t.license_plate).trim().toUpperCase());
      if (t.vin) s.add(String(t.vin).trim().toUpperCase());
      if (t.vehicle_id) s.add(String(t.vehicle_id).trim().toUpperCase());
    }
    return s;
  }, [catalog.trucks]);

  const filteredTrucks = useMemo(() => {
    const q = search.trim().toLowerCase();
    return catalog.trucks.filter((t) => {
      if (!q) return true;
      return (`${t.license_plate || ""} ${t.vin || ""} ${t.model || ""} ${t.company_name || ""}`)
        .toLowerCase().includes(q);
    });
  }, [catalog.trucks, search]);

  const intanglesUnmapped = useMemo(() => {
    return intangles.vehicles.filter((v) => {
      if (!v.plate) return false;
      return !mappedPlateSet.has(String(v.plate).trim().toUpperCase());
    });
  }, [intangles.vehicles, mappedPlateSet]);

  const toggleGroup = (id) => {
    setSel((s) => ({
      ...s,
      group_ids: s.group_ids.includes(id)
        ? s.group_ids.filter((x) => x !== id)
        : [...s.group_ids, id],
    }));
  };
  const toggleTruck = (id) => {
    setSel((s) => ({
      ...s,
      truck_ids: s.truck_ids.includes(id)
        ? s.truck_ids.filter((x) => x !== id)
        : [...s.truck_ids, id],
    }));
  };

  const addExtra = (k) => {
    const norm = String(k || "").trim().toUpperCase();
    if (!norm) return;
    setSel((s) => s.extra_keys.includes(norm) ? s : { ...s, extra_keys: [...s.extra_keys, norm] });
  };
  const removeExtra = (k) => {
    setSel((s) => ({ ...s, extra_keys: s.extra_keys.filter((x) => x !== k) }));
  };

  const onAddExtra = () => {
    const parts = extraInput.split(/[,\n]/).map((x) => x.trim()).filter(Boolean);
    if (!parts.length) return;
    for (const p of parts) addExtra(p);
    setExtraInput("");
  };

  const save = async () => {
    setSaving(true);
    setMsg(""); setErr("");
    try {
      const res = await fetch("/api/track/selection", {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(sel),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setMsg("Saved. Your map will refresh on the next 20-second tick.");
    } catch (e) {
      setErr(e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="admin-shell"><div className="admin-loading">Loading…</div></div>;

  return (
    <div className="admin-shell">
      <div className="admin-head">
        <h2>My fleet</h2>
        <p>Pick which vehicles appear on your live map. Your selection is private — it only affects what you see.</p>
      </div>

      <div className="admin-mode">
        <label className={sel.mode === "group" ? "active" : ""}>
          <input
            type="radio"
            checked={sel.mode === "group"}
            onChange={() => setSel((s) => ({ ...s, mode: "group" }))}
          />
          <span>By fleet group</span>
          <em>Pick customer-wise fleets — every truck in the fleet is included.</em>
        </label>
        <label className={sel.mode === "trucks" ? "active" : ""}>
          <input
            type="radio"
            checked={sel.mode === "trucks"}
            onChange={() => setSel((s) => ({ ...s, mode: "trucks" }))}
          />
          <span>By individual truck</span>
          <em>Hand-pick trucks one by one from battery_swap_db.</em>
        </label>
      </div>

      {sel.mode === "group" ? (
        <section className="admin-section">
          <h3>Fleet groups</h3>
          <div className="admin-groups">
            {catalog.groups.map((g) => {
              const checked = sel.group_ids.includes(g.id);
              return (
                <label key={g.id} className={`admin-group ${checked ? "checked" : ""}`}>
                  <input type="checkbox" checked={checked} onChange={() => toggleGroup(g.id)} />
                  <div>
                    <div className="g-name">{g.company_name || g.username}</div>
                    <div className="g-sub">{g.truck_count} trucks</div>
                  </div>
                </label>
              );
            })}
            {catalog.groups.length === 0 && <div className="empty">No fleet groups found in battery_swap_db.</div>}
          </div>
        </section>
      ) : (
        <section className="admin-section">
          <h3>Individual trucks</h3>
          <input
            className="admin-search"
            placeholder="Search plate / VIN / fleet…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div className="admin-trucks">
            {filteredTrucks.map((t) => {
              const checked = sel.truck_ids.includes(t.id);
              return (
                <label key={t.id} className={`admin-truck ${checked ? "checked" : ""}`}>
                  <input type="checkbox" checked={checked} onChange={() => toggleTruck(t.id)} />
                  <div className="t-plate">{t.license_plate}</div>
                  <div className="t-meta">{t.company_name || "—"}</div>
                  <div className="t-model">{t.model || ""}</div>
                </label>
              );
            })}
            {filteredTrucks.length === 0 && <div className="empty">No matches.</div>}
          </div>
        </section>
      )}

      <section className="admin-section">
        <h3>Extra vehicles</h3>
        <p className="hint">
          Plates or Intangles IDs that aren't in battery_swap_db (test rigs, demos, etc).
          Comma- or newline-separated.
        </p>
        <div className="admin-extra-input">
          <textarea
            rows={2}
            placeholder="MH46DC0367, MH46CU9742"
            value={extraInput}
            onChange={(e) => setExtraInput(e.target.value)}
          />
          <button onClick={onAddExtra}>Add</button>
        </div>
        <div className="admin-extra-chips">
          {sel.extra_keys.map((k) => (
            <span key={k} className="chip">
              {k}
              <button onClick={() => removeExtra(k)} aria-label="remove">×</button>
            </span>
          ))}
          {sel.extra_keys.length === 0 && <span className="empty">None.</span>}
        </div>

        {intangles.vehicles.length > 0 && (
          <div className="admin-discover">
            <label>
              <input
                type="checkbox"
                checked={showOnlyUnmapped}
                onChange={(e) => setShowOnlyUnmapped(e.target.checked)}
              />
              Show only Intangles vehicles not in battery_swap_db ({intanglesUnmapped.length})
            </label>
            <div className="admin-discover-list">
              {(showOnlyUnmapped ? intanglesUnmapped : intangles.vehicles).slice(0, 200).map((v) => (
                <button
                  key={v.id}
                  className="discover-pill"
                  onClick={() => addExtra(v.plate || v.id)}
                  title="Click to add as extra"
                >
                  {v.plate || v.tag || v.id}
                </button>
              ))}
            </div>
          </div>
        )}
      </section>

      <div className="admin-footer">
        {msg && <span className="ok">{msg}</span>}
        {err && <span className="err">{err}</span>}
        <button className="save" disabled={saving} onClick={save}>
          {saving ? "Saving…" : "Save selection"}
        </button>
      </div>
    </div>
  );
}
